// Tiny hash router.
// Phase 0: #/alarms, #/master/companies, #/master/sites, #/master/sites/:id, #/imports, #/imports/:id, #/ingest.
// Phase 1: #/console, #/console/:eventId, #/events, #/setup/..., #/reports, #/audit.
// Phase 2: #/wallboard, #/setup/groups.
// Phase 3: #/setup/ai, #/setup/ai/:siteId, #/qa, #/ai-report.
// Workspace rework (design/README.md, 2026-10-16): #/matrix/:eventId, the pop-out camera matrix (no app chrome).
// Phase 5: #/video/:eventId, the pop-out main video canvas (no app chrome), on the matrix's channel.
// Phase 6: #/erp-sync, #/erp-sync/:tab (overview, cutover, inbox, deliveries, digests).
// Query strings after the path are supported (#/master/sites?companyId=x).

import { useSyncExternalStore } from 'react';

export type ErpSyncTab = 'overview' | 'cutover' | 'inbox' | 'deliveries' | 'digests';
export const ERP_SYNC_TABS: readonly ErpSyncTab[] = ['overview', 'cutover', 'inbox', 'deliveries', 'digests'];

// danger: TEMPORARY, remove after test-data phase (docs/runbooks/temp-data-wipe.md).
export type SetupSection = 'sites' | 'dispositions' | 'missions' | 'authorities' | 'users' | 'groups' | 'ai' | 'talkdown' | 'workstations' | 'danger';

export type Route =
  | { name: 'console'; eventId: string | null }
  | { name: 'matrix'; eventId: string | null }
  | { name: 'video'; eventId: string | null }
  | { name: 'events'; state: 'open' | 'closed' | 'all' }
  | { name: 'setup'; section: SetupSection; id: string | null; tab: string | null }
  | { name: 'wallboard' }
  | { name: 'qa'; tab: 'pending' | 'reviewed'; eventId: string | null }
  | { name: 'aiReport'; days: number }
  | { name: 'reports' }
  | { name: 'audit' }
  | { name: 'alarms'; siteId: string | null; flag: string | null }
  | { name: 'companies' }
  | { name: 'sites'; companyId: string | null }
  | { name: 'site'; id: string; tab: string | null }
  | { name: 'imports' }
  | { name: 'import'; id: string }
  | { name: 'ingest' }
  | { name: 'erpSync'; tab: ErpSyncTab }
  | { name: 'notFound'; path: string };

export const DEFAULT_PATH = '/alarms';

function subscribe(cb: () => void): () => void {
  window.addEventListener('hashchange', cb);
  return () => window.removeEventListener('hashchange', cb);
}

function currentHash(): string {
  return window.location.hash;
}

const SETUP_SECTIONS: readonly SetupSection[] = ['sites', 'dispositions', 'missions', 'authorities', 'users', 'groups', 'ai', 'talkdown', 'workstations', 'danger'];
export const REPORT_DAYS = [7, 14, 30, 90] as const;

export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#/, '') || DEFAULT_PATH;
  const [pathPart = '', queryPart = ''] = raw.split('?', 2);
  const q = new URLSearchParams(queryPart);
  const segs = pathPart.split('/').filter(Boolean).map((s) => {
    try {
      return decodeURIComponent(s);
    } catch {
      return s;
    }
  });
  const [a, b, c] = segs;
  if (a === 'console' && segs.length <= 2) return { name: 'console', eventId: b ?? null };
  if (a === 'matrix' && segs.length <= 2) return { name: 'matrix', eventId: b ?? null };
  if (a === 'video' && segs.length <= 2) return { name: 'video', eventId: b ?? null };
  if (a === 'events' && segs.length === 1) {
    const st = q.get('state');
    return { name: 'events', state: st === 'closed' || st === 'all' ? st : 'open' };
  }
  if (a === 'setup' && segs.length <= 3) {
    const section = (SETUP_SECTIONS as readonly string[]).includes(b ?? '') ? (b as SetupSection) : 'sites';
    if (!b || section === b) return { name: 'setup', section, id: c ?? null, tab: q.get('tab') };
  }
  if (a === 'wallboard' && segs.length === 1) return { name: 'wallboard' };
  if (a === 'qa' && segs.length <= 2) return { name: 'qa', tab: q.get('tab') === 'reviewed' ? 'reviewed' : 'pending', eventId: b ?? null };
  if (a === 'ai-report' && segs.length === 1) {
    const d = Number(q.get('days'));
    return { name: 'aiReport', days: (REPORT_DAYS as readonly number[]).includes(d) ? d : 14 };
  }
  if (a === 'reports' && segs.length === 1) return { name: 'reports' };
  if (a === 'audit' && segs.length === 1) return { name: 'audit' };
  if (a === 'alarms' && segs.length === 1) return { name: 'alarms', siteId: q.get('siteId'), flag: q.get('flag') };
  if (a === 'master') {
    if (!b || (b === 'companies' && segs.length === 2)) return { name: 'companies' };
    if (b === 'sites' && segs.length === 2) return { name: 'sites', companyId: q.get('companyId') };
    if (b === 'sites' && c && segs.length === 3) return { name: 'site', id: c, tab: q.get('tab') };
  }
  if (a === 'imports' && segs.length === 1) return { name: 'imports' };
  if (a === 'imports' && b && segs.length === 2) return { name: 'import', id: b };
  if (a === 'ingest' && segs.length === 1) return { name: 'ingest' };
  if (a === 'erp-sync' && segs.length <= 2 && (!b || (ERP_SYNC_TABS as readonly string[]).includes(b))) {
    return { name: 'erpSync', tab: (b as ErpSyncTab | undefined) ?? 'overview' };
  }
  return { name: 'notFound', path: pathPart };
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, currentHash, () => '');
  return parseRoute(hash);
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  const target = path.startsWith('#') ? path : `#${path}`;
  if (opts.replace) {
    window.history.replaceState(null, '', target);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = target;
  }
}

const e = encodeURIComponent;

export const href = {
  console: (eventId?: string | null) => (eventId ? `#/console/${e(eventId)}` : '#/console'),
  matrix: (eventId: string) => `#/matrix/${e(eventId)}`,
  video: (eventId: string) => `#/video/${e(eventId)}`,
  events: (state?: 'open' | 'closed' | 'all') => (state && state !== 'open' ? `#/events?state=${state}` : '#/events'),
  setup: (section: SetupSection = 'sites', id?: string | null, tab?: string | null) =>
    `#/setup/${section}${id ? `/${e(id)}` : ''}${tab ? `?tab=${e(tab)}` : ''}`,
  wallboard: () => '#/wallboard',
  qa: (tab: 'pending' | 'reviewed' = 'pending', eventId?: string | null) => `#/qa${eventId ? `/${e(eventId)}` : ''}${tab === 'reviewed' ? '?tab=reviewed' : ''}`,
  aiReport: (days = 14) => (days === 14 ? '#/ai-report' : `#/ai-report?days=${days}`),
  reports: () => '#/reports',
  audit: () => '#/audit',
  alarms: (siteId?: string | null, flag?: string | null) => {
    const p = new URLSearchParams({ ...(siteId ? { siteId } : {}), ...(flag ? { flag } : {}) }).toString();
    return p ? `#/alarms?${p}` : '#/alarms';
  },
  companies: () => '#/master/companies',
  sites: (companyId?: string | null) => (companyId ? `#/master/sites?companyId=${e(companyId)}` : '#/master/sites'),
  site: (id: string, tab?: string) => `#/master/sites/${e(id)}${tab ? `?tab=${e(tab)}` : ''}`,
  imports: () => '#/imports',
  importRecord: (id: string) => `#/imports/${e(id)}`,
  ingest: () => '#/ingest',
  erpSync: (tab?: ErpSyncTab) => (tab && tab !== 'overview' ? `#/erp-sync/${tab}` : '#/erp-sync'),
};
